import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Settings } from 'lucide-react';
import { useIsAdmin, gustoKeys, isGustoEnvEnabled } from '../hooks';
import { GustoConnectionCard } from '../components/admin';
import { Alert, AlertDescription } from '../components/ui/alert';
import { AlertCircle, CheckCircle } from 'lucide-react';

const IntegrationsSettings: React.FC = () => {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAdmin, isLoading: adminLoading } = useIsAdmin();

  const [callbackAlert, setCallbackAlert] = React.useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  useEffect(() => {
    if (!adminLoading && (!isAdmin || !isGustoEnvEnabled())) {
      navigate('/dashboard');
    }
  }, [isAdmin, adminLoading, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    if (params.has('gusto_connected')) {
      setCallbackAlert({ type: 'success', message: t('gusto.callbackSuccess') });
      queryClient.invalidateQueries({ queryKey: gustoKeys.all });
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.has('gusto_error')) {
      const errorDescription = params.get('gusto_error_description') || params.get('gusto_error') || '';
      setCallbackAlert({
        type: 'error',
        message: t('gusto.callbackError', { error: errorDescription }),
      });
      window.history.replaceState({}, '', window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (adminLoading || !isAdmin || !isGustoEnvEnabled()) {
    return null;
  }

  return (
    <div className="container mx-auto py-8 px-4 max-w-6xl" data-testid="integrations-settings-page">
      <div className="flex items-center gap-3 mb-6">
        <Settings className="h-8 w-8" />
        <h1 className="text-3xl font-bold" data-testid="integrations-settings-title">
          {t('integrations.title')}
        </h1>
      </div>

      <div className="space-y-6">
        {callbackAlert && (
          <Alert
            variant={callbackAlert.type === 'success' ? 'success' : 'destructive'}
            data-testid="gusto-callback-alert"
          >
            {callbackAlert.type === 'success' ? (
              <CheckCircle className="h-4 w-4" />
            ) : (
              <AlertCircle className="h-4 w-4" />
            )}
            <AlertDescription>{callbackAlert.message}</AlertDescription>
          </Alert>
        )}

        <GustoConnectionCard />
      </div>
    </div>
  );
};

export default IntegrationsSettings;
